import React, { useState, useMemo, useCallback } from 'react';
import {
  Pane,
  Tablist,
  Tab,
  TextInputField,
  Textarea,
  Button,
  FileUploader,
  FileCard,
  Heading,
  Text,
  Dialog,
  Select,
  TagInput,
  Spinner,
} from 'evergreen-ui';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import 'pdfjs-dist/build/pdf.worker.mjs';
import './AdminTour.css';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

// Single File Uploader Component
function FileUploaderSingle({ label, description, maxSizeInBytes, maxFiles, accept, onChange, values }) {
  const [fileRejections, setFileRejections] = useState([]);

  const handleChange = useCallback(
    (files) => {
      if (files[0]) {
        if (files[0].size > maxSizeInBytes) {
          setFileRejections([{ file: files[0], errors: [{ message: `File size exceeds ${maxSizeInBytes / 1024 ** 2} MB` }] }]);
          return;
        }
        setFileRejections([]);
        onChange([files[0]]);
      } else {
        onChange([]);
      }
    },
    [maxSizeInBytes, onChange]
  );

  const handleRejected = useCallback(
    (rejections) => {
      setFileRejections(rejections);
    },
    []
  );

  const handleRemove = useCallback(
    () => {
      setFileRejections([]);
      onChange([]);
    },
    [onChange]
  );

  return (
    <Pane>
      <FileUploader
        label={label}
        description={description}
        maxSizeInBytes={maxSizeInBytes}
        maxFiles={maxFiles}
        accept={accept}
        onChange={handleChange}
        onRejected={handleRejected}
        values={values}
        renderFile={(file) => {
          const fileRejection = fileRejections.find((rejection) => rejection.file === file);
          return (
            <FileCard
              key={file.name}
              name={file.name}
              sizeInBytes={file.size}
              type={file.type}
              onRemove={handleRemove}
              isInvalid={!!fileRejection}
              validationMessage={fileRejection ? fileRejection.errors.map((e) => e.message).join(', ') : null}
            />
          );
        }}
      />
    </Pane>
  );
}

// Multiple File Uploader Component
function FileUploaderMultiple({ label, description, maxSizeInBytes, maxFiles, accept, onChange, values }) {
  const [fileRejections, setFileRejections] = useState([]);

  const rebaseFiles = useCallback(
    (files, { acceptedMimeTypes, maxFiles, maxSizeInBytes }) => {
      const accepted = [];
      const rejected = [];

      files.forEach((file, index) => {
        if (index >= maxFiles) {
          rejected.push({ file, errors: [{ message: 'Exceeds maximum number of files' }] });
        } else if (file.size > maxSizeInBytes) {
          rejected.push({ file, errors: [{ message: `File size exceeds ${maxSizeInBytes / 1024 ** 2} MB` }] });
        } else if (!acceptedMimeTypes.includes(file.type)) {
          rejected.push({ file, errors: [{ message: 'Invalid file type' }] });
        } else {
          accepted.push(file);
        }
      });

      return { accepted, rejected };
    },
    []
  );

  const handleChange = useCallback(
    (files) => {
      const newFiles = files.length ? files : values;
      const { accepted, rejected } = rebaseFiles(newFiles, {
        acceptedMimeTypes: accept,
        maxFiles,
        maxSizeInBytes,
      });
      setFileRejections(rejected);
      onChange(accepted);
    },
    [values, accept, maxFiles, maxSizeInBytes, onChange, rebaseFiles]
  );

  const handleRejected = useCallback(
    (rejections) => {
      setFileRejections(rejections);
    },
    []
  );

  const handleRemove = useCallback(
    (fileToRemove) => {
      const updatedFiles = values.filter((file) => file !== fileToRemove);
      const updatedRejections = fileRejections.filter(({ file }) => file !== fileToRemove);
      const { accepted, rejected } = rebaseFiles([...updatedFiles, ...updatedRejections.map(({ file }) => file)], {
        acceptedMimeTypes: accept,
        maxFiles,
        maxSizeInBytes,
      });
      setFileRejections(rejected);
      onChange(accepted);
    },
    [values, fileRejections, accept, maxFiles, maxSizeInBytes, onChange, rebaseFiles]
  );

  const fileCountOverLimit = values.length + fileRejections.length - maxFiles;
  const fileCountError =
    fileCountOverLimit > 0
      ? `You can upload up to ${maxFiles} files. Please remove ${fileCountOverLimit} ${fileCountOverLimit === 1 ? 'file' : 'files'}.`
      : '';

  return (
    <Pane>
      <FileUploader
        label={label}
        description={description}
        maxSizeInBytes={maxSizeInBytes}
        maxFiles={maxFiles}
        accept={accept}
        onChange={handleChange}
        onRejected={handleRejected}
        values={values}
        renderFile={(file, index) => {
          const fileRejection = fileRejections.find(
            (rejection) => rejection.file === file && rejection.errors[0].message !== 'Exceeds maximum number of files'
          );
          return (
            <Pane key={`${file.name}-${index}`}>
              {index === 0 && fileCountOverLimit > 0 && (
                <Text color="red" marginBottom={8}>
                  {fileCountError}
                </Text>
              )}
              <FileCard
                name={file.name}
                sizeInBytes={file.size}
                type={file.type}
                onRemove={() => handleRemove(file)}
                isInvalid={!!fileRejection}
                validationMessage={fileRejection ? fileRejection.errors.map((e) => e.message).join(', ') : null}
              />
            </Pane>
          );
        }}
      />
    </Pane>
  );
}

// Main AdminTour Component
function AdminTour() {
  const initialFormData = useMemo(
    () => ({
      country: '',
      safari_type: '',
      destination_title: '',
      destination_name: '',
      duration: '',
      price: '',
      image_preview: null,
      info_overview: '',
      info_highlight: '',
      departure: '',
      included: [],
      excluded: [],
      travel_plan_overview: '',
      travel_plan_day_title_1: '',
      travel_plan_day_description_1: '',
      travel_plan_day_title_2: '',
      travel_plan_day_description_2: '',
      travel_plan_day_title_3: '',
      travel_plan_day_description_3: '',
      travel_plan_day_title_4: '',
      travel_plan_day_description_4: '',
      travel_plan_day_title_5: '',
      travel_plan_day_description_5: '',
      travel_plan_day_title_6: '',
      travel_plan_day_description_6: '',
      travel_plan_day_title_7: '',
      travel_plan_day_description_7: '',
      image_gallery: [],
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormData);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [docFile, setDocFile] = useState(null);
  const [docRejections, setDocRejections] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dialog State for all notifications
  const [dialog, setDialog] = useState({
    isShown: false,
    title: '',
    message: '',
    intent: 'none',
  });

  const tabs = ['Basic Info', 'Detailed Info', 'Image Gallery'];
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  // Show Dialog Helper
  const showDialog = (title, message, intent = 'none') => {
    setDialog({ isShown: true, title, message, intent });
  };

  // Download Tour Template
  const handleDownloadTemplate = async () => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Tour Template', bold: true, size: 28 })],
            }),
            new Paragraph(' '),
            new Paragraph({
              children: [
                new TextRun({ text: 'Country: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Safari Type: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Destination Title: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Destination Name: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Duration: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Price: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Info Overview: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Tour Highlights: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Departure: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Included: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Excluded: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Travel Plan Overview: ', bold: true }),
                new TextRun('____________________'),
              ],
            }),
            ...Array.from({ length: 7 }, (_, i) => [
              new Paragraph({
                children: [
                  new TextRun({ text: `Day ${i + 1} Title: `, bold: true }),
                  new TextRun('____________________'),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: `Day ${i + 1} Description: `, bold: true }),
                  new TextRun('____________________'),
                ],
              }),
            ]).flat(),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Tour_Template.docx';
    link.click();
  };

  // Handle document upload (PDF or Word)
  const handleDocUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setDocFile(file);
    setDocRejections([]);
    setExtracting(true);

    try {
      let rawText = '';
      if (file.type === 'application/pdf') {
        rawText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        rawText = await extractTextFromWord(file);
      } else {
        showDialog('Invalid File', 'Please upload a Word (.docx) or PDF file.', 'danger');
        setDocFile(null);
        return;
      }

      fillFieldsFromExtractedText(rawText);
      showDialog('Success', 'Fields auto-filled from the uploaded document!', 'success');
    } catch (err) {
      console.error('Extraction error:', err);
      showDialog('Error', 'Failed to read the document.', 'danger');
    } finally {
      setExtracting(false);
    }
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let allText = '';

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      const pageText = textContent.items
        .map((item) => item.str.trim())
        .filter(Boolean)
        .join(' ');

      allText += pageText + '\n';
    }

    // Normalize and inject newlines between fields
    let normalized = allText
      .replace(/\r/g, '\n')
      .replace(/\s{2,}/g, ' ')
      // --- Fix broken field labels ---
      .replace(/Ti\s*tle/gi, 'Title')
      .replace(/Des\s*cription/gi, 'Description')
      .replace(/Over\s*view/gi, 'Overview')
      .replace(/High\s*lights/gi, 'Highlights')
      // ensure newlines before known field labels
      .replace(
        /\s*(?=(Country|Safari Type|Destination Title|Destination Name|Duration|Price|Info Overview|Tour Highlights|Departure|Included|Excluded|Travel Plan Overview|Day\s*\d+\s*Title|Day\s*\d+\s*Description)\s*[:-])/gi,
        '\n'
      )
      // separate "Day X Description" from "Day X+1 Title" if merged
      .replace(/(Day\s*\d+\s*Description\s*[:-][^\n]*?)\s*(?=Day\s*\d+\s*Title\s*[:-])/gi, '$1\n')
      .replace(/\n{2,}/g, '\n')
      .trim();

    return normalized;
  };

  // Extract text from Word
  const extractTextFromWord = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  };

  // Fill form fields from extracted text
  const fillFieldsFromExtractedText = (rawText) => {
    if (!rawText) {
      showDialog('Warning', 'No text found in the document.', 'warning');
      return;
    }

    let text = rawText
      .replace(/\r/g, '\n')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n{2,}/g, '\n')
      .trim() + '\n';

    const fields = {};
    const regexes = {
      country: /Country\s*[:-]\s*(.*)/i,
      safari_type: /Safari Type\s*[:-]\s*(.*)/i,
      destination_title: /Destination Title\s*[:-]\s*(.*)/i,
      destination_name: /Destination Name\s*[:-]\s*(.*)/i,
      duration: /Duration\s*[:-]\s*(.*)/i,
      price: /Price\s*[:-]\s*(.*)/i,
      info_overview: /Info Overview\s*[:-]\s*([\s\S]*?)(?=\n(?:Tour Highlights|Departure|Included|Excluded|Travel Plan Overview|Day|$))/i,
      info_highlight: /Tour Highlights\s*[:-]\s*([\s\S]*?)(?=\n(?:Departure|Included|Excluded|Travel Plan Overview|Day|$))/i,
      departure: /Departure\s*[:-]\s*(.*)/i,
      included: /Included\s*[:-]\s*(.*)/i,
      excluded: /Excluded\s*[:-]\s*(.*)/i,
      travel_plan_overview: /Travel Plan Overview\s*[:-]\s*([\s\S]*?)(?=\n(?:Day|$))/i,
    };

    // Extract normal fields
    for (const [key, regex] of Object.entries(regexes)) {
      const match = text.match(regex);
      if (match && match[1]) fields[key] = match[1].trim();
    }

    // Extract all day titles and descriptions correctly
    for (let i = 1; i <= 15; i++) {
      const titleRegex = new RegExp(
        `Day\\s*${i}\\s*Title\\s*[:-]\\s*([\\s\\S]*?)(?=Day\\s*${i}\\s*Description\\s*[:-]|$)`,
        'i'
      );
      const descRegex = new RegExp(
        `Day\\s*${i}\\s*Description\\s*[:-]\\s*([\\s\\S]*?)(?=(?:Day\\s*\\d+\\s*Title|$))`,
        'i'
      );

      const titleMatch = text.match(titleRegex);
      const descMatch = text.match(descRegex);

      if (titleMatch && titleMatch[1])
        fields[`travel_plan_day_title_${i}`] = titleMatch[1].trim();
      if (descMatch && descMatch[1])
        fields[`travel_plan_day_description_${i}`] = descMatch[1].trim();
    }

    if (fields.included)
      fields.included = fields.included.split(',').map((x) => x.trim()).filter(Boolean);
    if (fields.excluded)
      fields.excluded = fields.excluded.split(',').map((x) => x.trim()).filter(Boolean);

    setFormData((prev) => ({ ...prev, ...fields }));
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle tag input changes
  const handleTagInputChange = (field, values) => {
    setFormData((prev) => ({ ...prev, [field]: values }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    showDialog('Processing', 'Uploading tour data...', 'none');

    // Validate required fields
    const requiredFields = ['country', 'safari_type', 'destination_title', 'destination_name', 'duration', 'price', 'image_preview'];
    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length) {
      showDialog(
        'Missing Fields',
        `Please fill in: ${missingFields.join(', ')}`,
        'danger'
      );
      setIsSubmitting(false);
      return;
    }

    // Create FormData object
    const formDataToSend = new FormData();

    // Append non-file and non-array fields
    for (let key in formData) {
      if (key === 'image_gallery' || key === 'image_preview') {
        continue; // Handle files separately
      } else if (Array.isArray(formData[key])) {
        formDataToSend.append(key, formData[key].join(','));
      } else if (formData[key] !== null && formData[key] !== undefined) {
        formDataToSend.append(key, formData[key]);
      }
    }

    // Append image_preview (single file)
    if (formData.image_preview) {
      formDataToSend.append('image_preview', formData.image_preview);
    }

    // Append image_gallery files
    formData.image_gallery.forEach((file, index) => {
      if (file && index < 9) {
        formDataToSend.append(`image_gallery_${index + 1}`, file);
      }
    });

    try {
      const response = await fetch(`${API_BASE_URL}/tours`, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error uploading tour data');
      }

      // Reset form after successful submission
      setFormData(initialFormData);
      setDocFile(null);
      setDocRejections([]);
      showDialog('Success', 'Tour uploaded successfully!', 'success');
    } catch (error) {
      console.error('Error uploading tour data:', error);
      showDialog('Upload Failed', `Failed to upload tour data: ${error.message}`, 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Pane className="admin-tour-container">
      <h2 className="tour-title">Upload New Tour</h2>
      <form className="admin-tour-form" onSubmit={handleSubmit}>
        <div className="columns-container">
          {/* Left Column: Manual Entry */}
          <div className="left-column">
            <Tablist marginBottom={24}>
              {tabs.map((tab, index) => (
                <Tab
                  key={tab}
                  isSelected={index === selectedIndex}
                  onSelect={() => setSelectedIndex(index)}
                >
                  {tab}
                </Tab>
              ))}
            </Tablist>

            {selectedIndex === 0 && (
              <Pane display="flex" flexDirection="column" gap={16}>
                <Pane>
                  <label>Country</label>
                  <Select
                    value={formData.country}
                    onChange={(e) => handleSelectChange('country', e.target.value)}
                    width="100%"
                    required
                  >
                    <option value="">Select Country</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Uganda">Uganda</option>
                    <option value="Tanzania">Tanzania</option>
                  </Select>
                </Pane>

                <Pane>
                  <label>Safari Type</label>
                  <Select
                    value={formData.safari_type}
                    onChange={(e) => handleSelectChange('safari_type', e.target.value)}
                    width="100%"
                    required
                  >
                    <option value="">Select Safari Type</option>
                    <option value="Wildlife">Wildlife</option>
                    <option value="Excursions">Excursions</option>
                    <option value="Cultural">Cultural</option>
                    <option value="Adventure">Adventure</option>
                    <option value="Beach">Beach</option>
                    <option value="Luxury">Luxury</option>
                  </Select>
                </Pane>

                <TextInputField
                  label="Destination Title"
                  name="destination_title"
                  placeholder="Enter a title (e.g. The Great Wildebeest Migration)"
                  value={formData.destination_title}
                  onChange={handleChange}
                  required
                />
                <TextInputField
                  label="Destination Name"
                  name="destination_name"
                  placeholder="Enter a destination name (e.g. Maasai Mara National Reserve)"
                  value={formData.destination_name}
                  onChange={handleChange}
                  required
                />
                <TextInputField
                  label="Duration (days)"
                  name="duration"
                  type="number"
                  min={1}
                  placeholder="Enter number of days (e.g. 7)"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                />
                <TextInputField
                  label="Price (USD)"
                  name="price"
                  type="number"
                  min={0}
                  placeholder="Enter the price (e.g. 1500)"
                  value={formData.price}
                  onChange={handleChange}
                  required
                />
                <div className="image-picker">
                  <label>Preview Image</label>
                  <FileUploaderSingle
                    label="Upload Preview Image"
                    description="Upload 1 image, max 1 MB"
                    maxSizeInBytes={1 * 1024 ** 2}
                    maxFiles={1}
                    accept={['image/*']}
                    onChange={(files) => setFormData((prev) => ({ ...prev, image_preview: files[0] || null }))}
                    values={formData.image_preview ? [formData.image_preview] : []}
                  />
                </div>
              </Pane>
            )}

            {selectedIndex === 1 && (
              <Pane display="flex" flexDirection="column" gap={16}>
                <Pane>
                  <label>Info Overview</label>
                  <Textarea
                    name="info_overview"
                    placeholder="Enter an overview of the tour"
                    value={formData.info_overview}
                    onChange={handleChange}
                    minHeight={120}
                  />
                </Pane>
                <Pane>
                  <label>Tour Highlights</label>
                  <Textarea
                    name="info_highlight"
                    placeholder="Enter key highlights"
                    value={formData.info_highlight}
                    onChange={handleChange}
                    minHeight={120}
                  />
                </Pane>
                <TextInputField
                  label="Departure"
                  name="departure"
                  placeholder="Enter the departure location (e.g. Nairobi)"
                  value={formData.departure}
                  onChange={handleChange}
                />
                <Pane>
                  <label>Included</label>
                  <TagInput
                    values={formData.included}
                    onChange={(values) => handleTagInputChange('included', values)}
                    inputProps={{ placeholder: 'Add included items (e.g. Meals, Transport)' }}
                  />
                </Pane>
                <Pane>
                  <label>Excluded</label>
                  <TagInput
                    values={formData.excluded}
                    onChange={(values) => handleTagInputChange('excluded', values)}
                    inputProps={{ placeholder: 'Add excluded items (e.g. Visa, Tips)' }}
                  />
                </Pane>
                <Pane>
                  <label>Travel Plan Overview</label>
                  <Textarea
                    name="travel_plan_overview"
                    placeholder="Enter an overview of the travel plan"
                    value={formData.travel_plan_overview}
                    onChange={handleChange}
                    minHeight={120}
                  />
                </Pane>
                {Array.from({ length: 7 }, (_, i) => {
                  const day = i + 1;
                  return (
                    <Pane key={day}>
                      <TextInputField
                        label={`Day ${day} Title`}
                        name={`travel_plan_day_title_${day}`}
                        placeholder={`Enter title for Day ${day}`}
                        value={formData[`travel_plan_day_title_${day}`]}
                        onChange={handleChange}
                      />
                      <Pane>
                        <label>{`Day ${day} Description`}</label>
                        <Textarea
                          name={`travel_plan_day_description_${day}`}
                          placeholder={`Enter details for Day ${day}`}
                          value={formData[`travel_plan_day_description_${day}`]}
                          onChange={handleChange}
                          minHeight={120}
                        />
                      </Pane>
                    </Pane>
                  );
                })}
              </Pane>
            )}

            {selectedIndex === 2 && (
              <Pane display="flex" flexDirection="column" gap={16}>
                <Heading size={500} marginBottom={16}>
                  Image Gallery
                </Heading>
                <div className="image-picker">
                  <label>Upload Gallery Images</label>
                  <FileUploaderMultiple
                    label="Upload Gallery Images"
                    description="Upload up to 9 images, max 50 MB each"
                    maxSizeInBytes={50 * 1024 ** 2}
                    maxFiles={9}
                    accept={['image/jpeg', 'image/png']}
                    onChange={(files) => setFormData((prev) => ({ ...prev, image_gallery: files }))}
                    values={formData.image_gallery}
                  />
                </div>
              </Pane>
            )}
          </div>

          {/* Divider */}
          <div className="divider" />

          {/* Right Column: File Upload */}
          <div className="right-column">
            <div className="file-section">
              <h4>Auto-fill from Template</h4>
              <p className="template-desc">
                Download the Word template, fill it, then upload it here.
              </p>
              <Button
                appearance="minimal"
                intent="success"
                onClick={handleDownloadTemplate}
                marginTop={8}
              >
                ⬇️ Download Template
              </Button>
              <hr className="template-divider" />
              <FileUploaderSingle
                label="Upload Filled Template"
                description="Word (.docx) or PDF, max 50 MB"
                maxSizeInBytes={50 * 1024 ** 2}
                maxFiles={1}
                accept={[
                  'application/pdf',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ]}
                onChange={handleDocUpload}
                values={docFile ? [docFile] : []}
              />
              {docRejections.length > 0 && (
                <ul className="file-rejections">
                  {docRejections.map(({ file, errors }) => (
                    <li key={file.name} className="rejection-item">
                      ❌ <strong>{file.name}</strong> — {errors.map((e) => e.message).join(', ')}
                    </li>
                  ))}
                </ul>
              )}
              {extracting && <p className="loading-text">Extracting text…</p>}
            </div>
          </div>
        </div>

        <Button
          appearance="primary"
          intent="success"
          height={44}
          iconBefore={isSubmitting ? <Spinner size={16} /> : 'add'}
          type="submit"
          isLoading={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Tour'}
        </Button>
      </form>

      {/* Popup Dialog */}
      <Dialog
        isShown={dialog.isShown}
        title={dialog.title}
        intent={dialog.intent}
        onCloseComplete={() => setDialog((prev) => ({ ...prev, isShown: false }))}
        confirmLabel={dialog.intent === 'success' || dialog.intent === 'danger' ? 'OK' : 'Close'}
        hasCancel={false}
      >
        <Text>{dialog.message}</Text>
      </Dialog>
    </Pane>
  );
}

export default AdminTour;